import { memo, useMemo } from 'react';
import { useTranslation } from '@betty/i18n';
import Timer from '../../common/Timer/Timer';
import Box from '../../common/Box/Box';
import Text from '../../common/Text/Text';
import Icon from '../../common/Icon/Icon';
import Image from '../../common/Image/Image';
import Button from '../../common/Button/Button';
import { RewardAwardedProps } from './RewardAwarded.types';

function RewardAwarded({
  title,
  rewardText,
  rewardIcon,
  rewardAmount,
  nextRewardText,
  nextAvailableAt,
  buttonLocNS,
  buttonLocKey,
  onButtonPress,
  light,
  ...rest
}: RewardAwardedProps) {
  const { i18n } = useTranslation();
  const rewardAmountFormatted = useMemo(
    () => new Intl.NumberFormat(i18n.language).format(rewardAmount),
    [i18n.language, rewardAmount],
  );

  const { bgColor, textColor, iconColor } = useMemo(
    () => ({
      bgColor: light ? 'primaryBackground' : 'whiteBackground',
      textColor: light ? 'accentSurfacePrimary' : 'darkSurfacePrimary',
      iconColor: light ? 'iconPrimary' : 'iconSecondary',
    }),
    [light],
  );

  const icon = (
    <Icon
      name={rewardIcon}
      color={iconColor}
      width="space-400"
      height="space-400"
    />
  );

  return (
    <Box alignItems="center" width="100%" gap="space-400" {...rest}>
      <Box gap="space-300">
        <Text variant="overline" color={textColor} textAlign="center">
          {title}
        </Text>
        <Text variant="h4" color={textColor} textAlign="center">
          {rewardAmountFormatted}
        </Text>
      </Box>
      <Box width="100%">
        <Box height="space-025" backgroundColor={bgColor} />
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          gap="space-300"
          paddingVertical="space-200"
        >
          {icon}
          <Text variant="callout" marginTop="space-100" color={textColor}>
            {rewardText}
          </Text>
          {icon}
        </Box>
        <Box backgroundColor={bgColor} variant="default" height="space-025" />
      </Box>
      <Image name="common.BettyCoinStack" width={200} height={155} />
      {nextAvailableAt ? (
        <Box
          flexDirection="row"
          alignItems="center"
          marginBottom="space-200"
          gap="space-050"
        >
          <Text variant="body-s" color={textColor}>
            {nextRewardText}
          </Text>
          <Timer
            variant="body-s-semi"
            color={textColor}
            time={nextAvailableAt}
          />
        </Box>
      ) : null}
      <Button
        width="100%"
        light={light}
        variant="primary"
        locNS={buttonLocNS}
        locKey={buttonLocKey}
        onPress={onButtonPress}
      />
    </Box>
  );
}

export default memo(RewardAwarded);
